import './AllRestaurants.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SectionHeading } from '../Typography';
import AllRestaurantsControls from './AllRestaurantsControls';
import RestaurantCard from './RestaurantCard';
import {
  fetchFilteredRestaurants,
  fetchRestaurants,
  fetchRestaurantsByCuisine,
  restaurantDetailPath,
  searchRestaurants,
} from '../../services/api/restaurantService';
import { DEFAULT_DISTANCE_MI, milesToKmString, normalizeCoords } from '../../utils/geo';
import { DINDIN_ASSET_ORIGIN } from '../../services/api/config';
import generalBanner from './img/general_banner.png';

/** Matches default `.allrestaurants-grid` columns in `AllRestaurants.scss` (grid view). */
function getGridColumnCount(width) {
  if (width <= 576) return 1;
  if (width <= 992) return 2;
  if (width <= 1400) return 3;
  return 4;
}

const ROWS_PER_PAGE = 3;

function getPageChunkSize(viewMode) {
  if (typeof window === 'undefined') return viewMode === 'list' ? ROWS_PER_PAGE : 12;
  if (viewMode === 'list') return ROWS_PER_PAGE;
  return getGridColumnCount(window.innerWidth) * ROWS_PER_PAGE;
}

/** Prevents the observer from chaining many loads while the sentinel stays “in view”. */
const INFINITE_ROOT_MARGIN = '0px 0px 100px 0px';
const MIN_LOADING_MORE_MS = 360;
const CHUNK_LOAD_COOLDOWN_MS = 650;

/** Raw `delivery_time` from API (restaurant or closest branch). */
function resolveRawDeliveryTime(restaurant) {
  if (!restaurant || typeof restaurant !== 'object') return '';
  const top = restaurant.delivery_time ?? restaurant.deliveryTime;
  if (top != null && String(top).trim() !== '') return String(top).trim();
  const branches = Array.isArray(restaurant.branches) ? restaurant.branches : [];
  if (!branches.length) return '';
  let best = '';
  let bestDist = Infinity;
  for (const b of branches) {
    const dt = b?.delivery_time ?? b?.deliveryTime;
    if (dt == null || String(dt).trim() === '') continue;
    const d = parseFloat(b.distance ?? b.branch_distance_km ?? '');
    const str = String(dt).trim();
    if (Number.isFinite(d) && d >= 0) {
      if (d < bestDist) {
        bestDist = d;
        best = str;
      }
    } else if (!best) {
      best = str;
    }
  }
  return best;
}

/**
 * Parses API `delivery_time` values.
 * Canonical shape from backend: `"1802 minutes"` (digits + `minutes` / `minute`).
 */
function parseMinutesFromDeliveryValue(value) {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) return Math.round(value);
  const s = String(value).trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  // Match `minutes` / `minute` before `min` so we never split inside the word "minutes".
  const minutesWord = lower.match(/(\d+(?:\.\d+)?)\s*minutes?\b/);
  if (minutesWord) return Math.round(parseFloat(minutesWord[1]));
  const minOnly = lower.match(/^(\d+(?:\.\d+)?)\s*min\b/);
  if (minOnly) return Math.round(parseFloat(minOnly[1]));
  const hrMatch = lower.match(/^(\d+(?:\.\d+)?)\s*(?:hr|hrs|hour|hours)\b/);
  if (hrMatch) return Math.round(parseFloat(hrMatch[1]) * 60);
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  const n = parseFloat(s);
  if (Number.isFinite(n) && n > 0) return Math.round(n);
  return null;
}

/** Do not show ETA on cards when over this many minutes (avoids absurd API values like hundreds of hours). */
const MAX_DELIVERY_MINUTES_TO_SHOW = 60;

/** Turns total minutes into a short label (e.g. `45 min`, `1 hr`). Only used when minutes ≤ {@link MAX_DELIVERY_MINUTES_TO_SHOW}. */
function formatMinutesLabel(totalMinutes) {
  const m = Math.max(0, Math.round(Number(totalMinutes)));
  if (!Number.isFinite(m) || m <= 0) return '';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (rem === 0) return `${h} hr`;
  return `${h} hr ${rem} min`;
}

/** Human-readable delivery time for cards; returns empty string when unknown or over one hour (so UI hides it). */
function resolveDeliveryTimeLabel(restaurant) {
  const raw = resolveRawDeliveryTime(restaurant);
  if (!raw) return '';
  const minutes = parseMinutesFromDeliveryValue(raw);
  if (minutes != null) {
    if (minutes > MAX_DELIVERY_MINUTES_TO_SHOW) return '';
    const label = formatMinutesLabel(minutes);
    if (label) return label;
  }
  // If API value isn't parseable into a real duration, hide it instead of showing a static placeholder.
  return '';
}

/** Resolves banner/logo URLs from API (absolute or relative). */
function resolveMediaUrl(url) {
  const u = (url || '').trim();
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/')) return `${DINDIN_ASSET_ORIGIN}${u}`;
  return `${DINDIN_ASSET_ORIGIN}/${u}`;
}

function primaryCuisineLabel(cuisines) {
  if (!Array.isArray(cuisines) || !cuisines.length) return '';
  const names = cuisines.map((c) => (c?.name || '').trim()).filter(Boolean);
  return names[0] || '';
}

function resolveRatingValue(restaurant) {
  const v =
    restaurant?.rating ??
    restaurant?.google_rating ??
    restaurant?.googleRate ??
    restaurant?.rate;
  if (v == null) return '';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : '';
}

function resolveReviewsCountValue(restaurant) {
  const v =
    restaurant?.reviewsCount ??
    restaurant?.reviews_count ??
    restaurant?.review_count ??
    restaurant?.total_reviews ??
    restaurant?.google_reviews ??
    restaurant?.google_reviews_count;
  if (v == null) return '';
  const n = Number(v);
  return Number.isFinite(n) ? String(Math.max(0, Math.round(n))) : '';
}

export default function AllRestaurants({ coordsOverride = null, cuisineId = null, onClearCuisine }) {
    const [restaurants, setRestaurants] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [visibleCount, setVisibleCount] = useState(() => getPageChunkSize('grid'));
    const [loadingMore, setLoadingMore] = useState(false);
    const [appliedDistanceMi, setAppliedDistanceMi] = useState(null);
    const [submittedQuery, setSubmittedQuery] = useState('');
    const [sortOrder, setSortOrder] = useState('desc');
    const [isRestaurantOfTheDay, setIsRestaurantOfTheDay] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    const effectiveCoords = useMemo(() => normalizeCoords(coordsOverride), [coordsOverride]);
    const hasCoords = !!effectiveCoords;
    const sentinelRef = useRef(null);
    const loadingMoreLockRef = useRef(false);
    const lastChunkLoadAtRef = useRef(0);
    const filteredLengthRef = useRef(0);
    const skipDefaultDistanceRef = useRef(false);

    useEffect(() => {
      skipDefaultDistanceRef.current = false;
    }, [effectiveCoords?.latitude, effectiveCoords?.longitude]);

    useEffect(() => {
      if (!hasCoords) {
        setAppliedDistanceMi(null);
        return;
      }
      if (skipDefaultDistanceRef.current) {
        return;
      }
      setAppliedDistanceMi(DEFAULT_DISTANCE_MI);
    }, [hasCoords]);

    const isDistanceFilterActive = appliedDistanceMi != null;

    const handleDistanceApply = (mi) => {
      skipDefaultDistanceRef.current = false;
      setAppliedDistanceMi(mi);
    };

    const handleDistanceClear = () => {
      skipDefaultDistanceRef.current = true;
      setAppliedDistanceMi(null);
    };

    useEffect(() => {
        const ac = new AbortController();
        let cancelled = false;
        (async () => {
          try {
            setLoading(true);
            setError(null);
            const coordArgs = hasCoords ? effectiveCoords : null;
            const distanceKm = appliedDistanceMi != null ? milesToKmString(appliedDistanceMi) : null;

            let list;
            if (cuisineId) {
              ({ restaurants: list } = await fetchRestaurantsByCuisine(cuisineId, {
                ...(coordArgs || {}),
                signal: ac.signal,
              }));
            } else if (coordArgs && isDistanceFilterActive && distanceKm) {
              ({ restaurants: list } = await fetchFilteredRestaurants({
                distance: `${distanceKm}km`,
                sort: sortOrder,
                isRestaurantOfTheDay,
                latitude: coordArgs.latitude,
                longitude: coordArgs.longitude,
                signal: ac.signal,
              }));
            } else {
              ({ restaurants: list } = await fetchRestaurants({
                ...(coordArgs || {}),
                sort: sortOrder,
                isRestaurantOfTheDay,
                signal: ac.signal,
              }));
            }
            if (!cancelled) setRestaurants(list);
          } catch (e) {
            if (e.name === 'AbortError') return;
            if (!cancelled) setError(e.message || 'Could not load restaurants');
          } finally {
            if (!cancelled) setLoading(false);
          }
        })();

        return () => {
          cancelled = true;
          ac.abort();
        };
    }, [effectiveCoords, cuisineId, appliedDistanceMi, sortOrder, isRestaurantOfTheDay, hasCoords, isDistanceFilterActive]);

    useEffect(() => {
      setQuery('');
      setSubmittedQuery('');
      setSearchResults([]);
    }, [cuisineId]);

    useEffect(() => {
      setVisibleCount(getPageChunkSize(viewMode));
      lastChunkLoadAtRef.current = 0;
    }, [submittedQuery]);

    useEffect(() => {
      // When switching layout, keep at least as many items visible as before (up to total).
      setVisibleCount((prev) => {
        const chunk = getPageChunkSize(viewMode);
        const maxLen = filteredRestaurants.length;
        if (!maxLen) return chunk;
        return Math.min(maxLen, Math.max(prev, chunk));
      });
      lastChunkLoadAtRef.current = 0;
    }, [viewMode]);

    const trimmedSubmitted = submittedQuery.trim();

    useEffect(() => {
      const q = trimmedSubmitted;
      const ac = new AbortController();
      let cancelled = false;

      if (!q) {
        setIsSearching(false);
        setSearchResults([]);
        return () => ac.abort();
      }

      (async () => {
        try {
          setIsSearching(true);
          setError(null);
          // Listings respect the distance filter; search is always global (name, description, menus).
          const searchOpts = { signal: ac.signal };
          if (hasCoords && effectiveCoords) {
            searchOpts.latitude = effectiveCoords.latitude;
            searchOpts.longitude = effectiveCoords.longitude;
          }
          const { restaurants: list } = await searchRestaurants(q, searchOpts);
          if (!cancelled) setSearchResults(list);
        } catch (e) {
          if (e.name === 'AbortError') return;
          if (!cancelled) setError(e.message || 'Could not search restaurants');
        } finally {
          if (!cancelled) setIsSearching(false);
        }
      })();

      return () => {
        cancelled = true;
        ac.abort();
      };
    }, [trimmedSubmitted, hasCoords, effectiveCoords]);

    const filteredRestaurants = useMemo(() => {
      if (trimmedSubmitted) return searchResults;
      return restaurants;
    }, [restaurants, searchResults, trimmedSubmitted]);

    filteredLengthRef.current = filteredRestaurants.length;

    const hasMore = !trimmedSubmitted && filteredRestaurants.length > visibleCount;

    useEffect(() => {
      const el = sentinelRef.current;
      if (!el) return;
      if (!hasMore) return;
      if (loading) return;

      let cancelled = false;
      let loadTimerId = 0;

      const obs = new IntersectionObserver(
        (entries) => {
          if (cancelled) return;
          const first = entries[0];
          if (!first?.isIntersecting) return;
          if (loadingMoreLockRef.current) return;

          const now = Date.now();
          if (now - lastChunkLoadAtRef.current < CHUNK_LOAD_COOLDOWN_MS) return;

          lastChunkLoadAtRef.current = now;
          loadingMoreLockRef.current = true;
          obs.unobserve(el);
          setLoadingMore(true);

          const chunk = getPageChunkSize(viewMode);
          const startedAt = Date.now();

          window.requestAnimationFrame(() => {
            if (cancelled) return;
            setVisibleCount((v) => Math.min(v + chunk, filteredLengthRef.current));

            const elapsed = Date.now() - startedAt;
            const wait = Math.max(0, MIN_LOADING_MORE_MS - elapsed);
            loadTimerId = window.setTimeout(() => {
              if (cancelled) return;
              setLoadingMore(false);
              loadingMoreLockRef.current = false;
              const target = sentinelRef.current;
              if (target) obs.observe(target);
            }, wait);
          });
        },
        { root: null, rootMargin: INFINITE_ROOT_MARGIN, threshold: 0 }
      );
      obs.observe(el);
      return () => {
        cancelled = true;
        window.clearTimeout(loadTimerId);
        obs.disconnect();
      };
    }, [hasMore, loading, filteredRestaurants.length, trimmedSubmitted, viewMode]);

    const cards = useMemo(() => {
        return filteredRestaurants.slice(0, visibleCount).map((r) => {
            const bannerResolved = resolveMediaUrl(
              r.pick_of_the_day_banner_url || r.banner_url
            );
            const logoResolved = resolveMediaUrl(r.logo_url || r.dark_logo_url);
            const description = (r.description || '').trim();
            const cuisineFromApi = primaryCuisineLabel(r.cuisines);

            return {
                key: r.id,
                to: restaurantDetailPath(r),
                // Always show a "banner" up top (API banner or default banner).
                imageSrc: bannerResolved || generalBanner,
                heroUsesLogo: false,
                // Always show a logo avatar (API logo or default handled in RestaurantCard).
                logoSrc: logoResolved,
                imageAlt: r.name || 'Restaurant',
                title: r.name || '',
                ...(description ? { description } : {}),
                rating: resolveRatingValue(r),
                reviewsCount: resolveReviewsCountValue(r),
                time: resolveDeliveryTimeLabel(r),
                ...(cuisineFromApi ? { cuisine: cuisineFromApi } : {}),
            };
        });
    }, [filteredRestaurants, visibleCount]);

    return <>
        <div className='container'>
            <div className='top_cusines' id="all-restaurants">
              <div className='allrestaurants-header d-flex align-items-center justify-content-between'>
                <SectionHeading>ALL RESTAURANTS</SectionHeading>
                {/* Controls row (and expandable filter row under it) */}
                <AllRestaurantsControls
                  searchValue={query}
                  onSearchChange={setQuery}
                  onSearchSubmit={() => setSubmittedQuery(query.trim())}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  hasCoords={hasCoords}
                  appliedDistanceMi={appliedDistanceMi}
                  onDistanceApply={handleDistanceApply}
                  onDistanceClear={handleDistanceClear}
                  sort={sortOrder}
                  onSortChange={setSortOrder}
                  isRestaurantOfTheDay={isRestaurantOfTheDay}
                  onRestaurantOfTheDayChange={setIsRestaurantOfTheDay}
                  showReset={
                    !!cuisineId ||
                    !!query.trim() ||
                    !!submittedQuery.trim() ||
                    isDistanceFilterActive ||
                    isRestaurantOfTheDay ||
                    sortOrder !== 'desc'
                  }
                  onReset={() => {
                    setQuery('');
                    setSubmittedQuery('');
                    skipDefaultDistanceRef.current = true;
                    setAppliedDistanceMi(null);
                    setSortOrder('desc');
                    setIsRestaurantOfTheDay(false);
                    onClearCuisine?.();
                  }}
                />
              </div>

              {error && (
                <p className="text-danger small mb-2" role="alert">
                  {error}
                </p>
              )}

              {/* Cards row */}
              <div
                className={`allrestaurants-grid${
                  viewMode === 'list' ? ' allrestaurants-grid--list' : ''
                }`}
              >
                {loading ? (
                  <p className="text-muted small allrestaurants-grid__status">Loading restaurants…</p>
                ) : null}

                {!loading && isSearching ? (
                  <p className="text-muted small allrestaurants-grid__status">Searching…</p>
                ) : null}

                {!loading &&
                    cards.map((c) => (
                        <RestaurantCard
                            key={c.key}
                            to={c.to}
                            logoSrc={c.logoSrc}
                            imageSrc={c.imageSrc}
                            heroUsesLogo={c.heroUsesLogo}
                            imageAlt={c.imageAlt}
                            title={c.title}
                            description={c.description}
                            rating={c.rating}
                            reviewsCount={c.reviewsCount}
                            time={c.time}
                            cuisine={c.cuisine}
                        />
                    ))}
              </div>

              {!loading && !error && trimmedSubmitted && !isSearching && !filteredRestaurants.length ? (
                <p className="text-muted small mb-0">No restaurants found.</p>
              ) : null}

              {!loading &&
              !error &&
              !trimmedSubmitted &&
              !filteredRestaurants.length &&
              hasCoords &&
              isDistanceFilterActive ? (
                <div className="allrestaurants-area-empty" role="status">
                  <p className="allrestaurants-area-empty__title">No restaurant found in your area</p>
                  <p className="allrestaurants-area-empty__text">
                    Try increasing the distance filter, or reset filters to see all restaurants.
                  </p>
                </div>
              ) : null}

              {/* Infinite scroll sentinel (only while more to load, or empty list) */}
              {!loading && !error && !trimmedSubmitted && (hasMore || !filteredRestaurants.length) ? (
                <div
                  className={`allrestaurants-sentinel${loadingMore ? ' allrestaurants-sentinel--loading' : ''}`}
                  ref={sentinelRef}
                  aria-busy={loadingMore}
                >
                  {hasMore && loadingMore ? (
                    <div className="allrestaurants-loading-more" role="status">
                      <svg
                        className="allrestaurants-loading-more__mark"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden
                      >
                        <path
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinejoin="round"
                          d="M4 10h16v11H4zM6 10l2-5h8l2 5M9 21v-6h6v6"
                        />
                      </svg>
                      <span className="allrestaurants-loading-more__spinner" aria-hidden />
                      <span className="allrestaurants-loading-more__text">Loading more…</span>
                    </div>
                  ) : null}
                  {!hasMore && !filteredRestaurants.length && !(hasCoords && isDistanceFilterActive) ? (
                    <p className="text-muted small mb-0">No restaurants found.</p>
                  ) : null}
                </div>
              ) : null}
            </div>
        </div>
    </>
}